/**
 * Résolution graphique — logique pure (Boîte à outils, 3e → 1re).
 *
 * Deux courbes, ou une courbe et la droite y = k. On cherche les x de
 * l'ensemble de définition où f(x) = g(x), f(x) < g(x)… : les zéros de
 * h = f − g découpent le domaine en morceaux de signe constant ; un morceau
 * est gardé si son milieu vérifie l'inégalité stricte, ses bornes si elles
 * appartiennent au domaine et vérifient l'inégalité large.
 *
 * Les courbes « données » (comme dans un sujet) passent par des points du
 * quadrillage : interpolation cubique MONOTONE (Fritsch–Butland), donc pas
 * de bosse cachée entre deux nœuds et un extremum pile sur un nœud. Une
 * abscisse lue entre deux nœuds est approchée (« ≈ −2,4 ») ; une abscisse
 * calculable s'écrit exactement (−1 ; 0,5 ; 1/3 ; √3 ; √1,5).
 *
 * Sur un domaine infini, les zéros sont cherchés bien au-delà de la fenêtre
 * (cinq largeurs de chaque côté) et les bouts infinis restent à part : une
 * solution n'est jamais confondue avec le bord du cadre.
 */

import type { Viewport } from '../../ui/frame';

/** espace insécable, autour de « ; » dans les ensembles */
const NB = String.fromCharCode(0xa0);

export type Cmp = '=' | '<' | '<=' | '>' | '>=';

export const CMP_TEXT: Record<Cmp, string> = {
  '=': '=',
  '<': '<',
  '<=': '≤',
  '>': '>',
  '>=': '≥',
};

export interface Fn {
  /** f(x), NaN hors du domaine */
  at: (x: number) => number;
  /** écriture (« x² », « x + 2 ») ; null pour une courbe donnée sans formule */
  formula: string | null;
  /** nœuds d'une courbe donnée : abscisses où la valeur est exacte */
  nodes?: ReadonlyArray<readonly [number, number]>;
}

export interface Domain {
  /** null : −∞ / +∞ */
  lo: number | null;
  hi: number | null;
  loClosed: boolean;
  hiClosed: boolean;
  /** valeurs interdites (1/x : 0) */
  holes: readonly number[];
  /** « [−4 ; 4] », « ℝ », « ℝ* », « [0 ; +∞[ » */
  text: string;
}

export interface Scenario {
  id: string;
  minRank: number;
  maxRank: number;
  /** puce de choix */
  label: string;
  f: Fn;
  /** null : la droite y = k (k au curseur) */
  g: Fn | null;
  domain: Domain;
  window: Viewport;
  /** k de départ et bornes du curseur (pas de 0,5) */
  k?: { start: number; min: number; max: number };
}

/* ---------- courbes données : cubique monotone par morceaux ---------- */

export function monotoneCubic(
  nodes: ReadonlyArray<readonly [number, number]>,
): (x: number) => number {
  const n = nodes.length;
  const xs = nodes.map((p) => p[0]);
  const ys = nodes.map((p) => p[1]);
  const h = xs.slice(1).map((x, i) => x - xs[i]!);
  const d = ys.slice(1).map((y, i) => (y - ys[i]!) / h[i]!);
  const m = xs.map((_, i) => {
    if (i === 0) return d[0]!;
    if (i === n - 1) return d[n - 2]!;
    const a = d[i - 1]!;
    const b = d[i]!;
    // extremum local (ou palier) : tangente horizontale
    if (a * b <= 0) return 0;
    const w1 = 2 * h[i]! + h[i - 1]!;
    const w2 = h[i]! + 2 * h[i - 1]!;
    return (w1 + w2) / (w1 / a + w2 / b);
  });
  return (x) => {
    if (!(x >= xs[0]! && x <= xs[n - 1]!)) return NaN;
    let i = 0;
    while (i < n - 2 && x >= xs[i + 1]!) i++;
    const t = (x - xs[i]!) / h[i]!;
    if (t === 0) return ys[i]!;
    if (t === 1) return ys[i + 1]!;
    const t2 = t * t;
    const t3 = t2 * t;
    return (
      (2 * t3 - 3 * t2 + 1) * ys[i]! +
      (t3 - 2 * t2 + t) * h[i]! * m[i]! +
      (-2 * t3 + 3 * t2) * ys[i + 1]! +
      (t3 - t2) * h[i]! * m[i + 1]!
    );
  };
}

const given = (nodes: ReadonlyArray<readonly [number, number]>): Fn => ({
  at: monotoneCubic(nodes),
  formula: null,
  nodes,
});
const formula = (text: string, at: (x: number) => number): Fn => ({ at, formula: text });

const closed = (lo: number, hi: number): Domain => ({
  lo,
  hi,
  loClosed: true,
  hiClosed: true,
  holes: [],
  text: `[${num(lo)}${NB};${NB}${num(hi)}]`,
});
const R: Domain = { lo: null, hi: null, loClosed: false, hiClosed: false, holes: [], text: 'ℝ' };

/** courbe d'un sujet : décroît, remonte, redescend ; extremums sur les nœuds */
const CURVE_A = given([
  [-4, 2],
  [-3, 0],
  [-2, -1],
  [-1, 0],
  [0, 2],
  [1, 3],
  [2, 2],
  [3, 0],
  [4, -1],
]);

/** une seconde courbe de sujet : monte, redescend, remonte */
const CURVE_B = given([
  [-4, -1],
  [-3, 1],
  [-2, 3],
  [-1, 1],
  [0, 0],
  [1, -1],
  [2, 1],
  [3, 3],
  [4, 2],
]);

export const SCENARIOS: readonly Scenario[] = [
  {
    id: 'curve-k',
    minRank: 4,
    maxRank: 6,
    label: 'Courbe et droite y = k',
    f: CURVE_A,
    g: null,
    domain: closed(-4, 4),
    window: { xMin: -5, xMax: 5, yMin: -4, yMax: 6 },
    k: { start: 0, min: -3, max: 5 },
  },
  {
    id: 'curve2-k',
    minRank: 4,
    maxRank: 6,
    label: 'Autre courbe et y = k',
    f: CURVE_B,
    g: null,
    domain: closed(-4, 4),
    window: { xMin: -5, xMax: 5, yMin: -4, yMax: 6 },
    k: { start: 1, min: -3, max: 5 },
  },
  {
    id: 'curve-line',
    minRank: 4,
    maxRank: 6,
    label: 'Courbe et droite',
    f: CURVE_A,
    g: formula('x + 2', (x) => x + 2),
    domain: closed(-4, 4),
    window: { xMin: -5, xMax: 5, yMin: -4, yMax: 6 },
  },
  {
    id: 'square-k',
    minRank: 4,
    maxRank: 6,
    label: 'x² et y = k',
    f: formula('x²', (x) => x * x),
    g: null,
    domain: R,
    window: { xMin: -4, xMax: 4, yMin: -2, yMax: 6 },
    k: { start: 4, min: -1, max: 5 },
  },
  {
    id: 'square-line',
    minRank: 5,
    maxRank: 6,
    label: 'x² et x + 2',
    f: formula('x²', (x) => x * x),
    g: formula('x + 2', (x) => x + 2),
    domain: R,
    window: { xMin: -4, xMax: 4, yMin: -2, yMax: 6 },
  },
  {
    id: 'inverse-line',
    minRank: 5,
    maxRank: 6,
    label: '1/x et x',
    f: formula('1/x', (x) => (x === 0 ? NaN : 1 / x)),
    g: formula('x', (x) => x),
    domain: { lo: null, hi: null, loClosed: false, hiClosed: false, holes: [0], text: 'ℝ*' },
    window: { xMin: -4, xMax: 4, yMin: -4, yMax: 4 },
  },
  {
    id: 'inverse-k',
    minRank: 5,
    maxRank: 6,
    label: '1/x et y = k',
    f: formula('1/x', (x) => (x === 0 ? NaN : 1 / x)),
    g: null,
    domain: { lo: null, hi: null, loClosed: false, hiClosed: false, holes: [0], text: 'ℝ*' },
    window: { xMin: -4, xMax: 4, yMin: -4, yMax: 4 },
    k: { start: 2, min: -3, max: 3 },
  },
  {
    id: 'cube-line',
    minRank: 5,
    maxRank: 6,
    label: 'x³ et x',
    f: formula('x³', (x) => x * x * x),
    g: formula('x', (x) => x),
    domain: R,
    window: { xMin: -2.5, xMax: 2.5, yMin: -2.5, yMax: 2.5 },
  },
  {
    id: 'sqrt-line',
    minRank: 5,
    maxRank: 6,
    label: '√x et x/2',
    f: formula('√x', (x) => (x < 0 ? NaN : Math.sqrt(x))),
    g: formula('x/2', (x) => x / 2),
    domain: {
      lo: 0,
      hi: null,
      loClosed: true,
      hiClosed: false,
      holes: [],
      text: `[0${NB};${NB}+∞[`,
    },
    window: { xMin: -1, xMax: 7, yMin: -2, yMax: 6 },
  },
  {
    id: 'abs-k',
    minRank: 5,
    maxRank: 6,
    label: '|x − 1| et y = k',
    f: formula('|x − 1|', (x) => Math.abs(x - 1)),
    g: null,
    domain: R,
    window: { xMin: -3, xMax: 5, yMin: -2, yMax: 6 },
    k: { start: 2, min: -1, max: 3.5 },
  },
  {
    id: 'trinome',
    minRank: 6,
    maxRank: 6,
    label: 'x² − 2x − 3 et 0',
    f: formula('x² − 2x − 3', (x) => x * x - 2 * x - 3),
    g: formula('0', () => 0),
    domain: R,
    window: { xMin: -3, xMax: 5, yMin: -5, yMax: 3 },
  },
  {
    id: 'parabola-line',
    minRank: 6,
    maxRank: 6,
    label: '−x² + 4 et 2x + 1',
    f: formula('−x² + 4', (x) => 4 - x * x),
    g: formula('2x + 1', (x) => 2 * x + 1),
    domain: R,
    window: { xMin: -6, xMax: 4, yMin: -6, yMax: 4 },
  },
];

export const scenarioById = (id: string) => SCENARIOS.find((s) => s.id === id) ?? SCENARIOS[0]!;

/** la seconde fonction : g, ou la constante k */
export const gOf = (s: Scenario, k: number): Fn => s.g ?? { at: () => k, formula: num(k) };

/* ---------- nombres ---------- */

/** « −2,5 », sans « −0 » */
export function num(v: number, digits = 6): string {
  const r = Number(v.toFixed(digits));
  return String(Object.is(r, -0) ? 0 : r)
    .replace('.', ',')
    .replace('-', '−');
}

export interface Exact {
  v: number;
  /** écriture : « −1 », « 1/2 », « −√3 », « 2,4 » (approchée) */
  text: string;
  exact: boolean;
}

/**
 * Écriture d'une abscisse : exacte si elle est rationnelle à petit
 * dénominateur ou ±√n ; sinon approchée au dixième (lecture graphique).
 */
export function exactText(v: number, approxDigits = 1): Exact {
  for (const q of [1, 2, 3, 4, 5, 6, 8, 10]) {
    const p = Math.round(v * q);
    if (Math.abs(v * q - p) < 1e-7) {
      if (q === 1 || [2, 4, 5, 8, 10].includes(q)) return { v, text: num(p / q), exact: true };
      const g = gcd(Math.abs(p), q);
      const sign = p < 0 ? '−' : '';
      return { v, text: `${sign}${Math.abs(p) / g}/${q / g}`, exact: true };
    }
  }
  const n = Math.round(v * v);
  if (n <= 200 && Math.abs(v * v - n) < 1e-7) {
    // ±√n, ou a√b si n = a²b
    let a = 1;
    let b = n;
    for (let d = 2; d * d <= b; d++)
      while (b % (d * d) === 0) {
        a *= d;
        b /= d * d;
      }
    return { v, text: `${v < 0 ? '−' : ''}${a > 1 ? a : ''}√${b}`, exact: true };
  }
  // x² = 1,5 : ±√1,5 (programme de 2nde : x² = k ⇔ x = ±√k)
  const v2 = v * v;
  if (Math.abs(v2 * 10 - Math.round(v2 * 10)) < 1e-7 && v2 <= 100)
    return { v, text: `${v < 0 ? '−' : ''}√${num(v2)}`, exact: true };
  return { v, text: num(v, approxDigits), exact: false };
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/* ---------- résolution ---------- */

export interface Bound {
  /** ±Infinity pour −∞ / +∞ */
  v: number;
  closed: boolean;
}
/** un intervalle, ou un point isolé (lo.v === hi.v) */
export interface Piece {
  lo: Bound;
  hi: Bound;
}

export interface Solution {
  pieces: Piece[];
  /** abscisses des points d'intersection dans le domaine */
  zeros: number[];
  /** texte « S = [−1 ; 2] » ou « S ≈ … » */
  text: string;
  /** au moins une borne lue (approchée) */
  approx: boolean;
}

const holds = (cmp: Cmp, h: number, eps = 1e-9) => {
  switch (cmp) {
    case '=':
      return Math.abs(h) <= eps;
    case '<':
      return h < -eps;
    case '<=':
      return h <= eps;
    case '>':
      return h > eps;
    case '>=':
      return h >= -eps;
  }
};

/** bornes de la recherche des zéros : le domaine, ou cinq largeurs de fenêtre au-delà */
function searchSpan(s: Scenario): [number, number] {
  const w = s.window.xMax - s.window.xMin;
  return [
    s.domain.lo ?? Math.floor(s.window.xMin - 5 * w),
    s.domain.hi ?? Math.ceil(s.window.xMax + 5 * w),
  ];
}

/** bornes de la partie visible du domaine (pour les ordonnées extrêmes) */
function visibleSpan(s: Scenario): [number, number] {
  return [s.domain.lo ?? s.window.xMin, s.domain.hi ?? s.window.xMax];
}

export const inDomain = (s: Scenario, x: number) => {
  const d = s.domain;
  if (d.holes.some((z) => Math.abs(z - x) < 1e-12)) return false;
  if (d.lo !== null && (x < d.lo - 1e-12 || (!d.loClosed && Math.abs(x - d.lo) < 1e-12)))
    return false;
  if (d.hi !== null && (x > d.hi + 1e-12 || (!d.hiClosed && Math.abs(x - d.hi) < 1e-12)))
    return false;
  return true;
};

/** zéros de h sur [a, b] (changements de signe affinés, contacts sans traversée compris) */
export function zerosOf(
  h: (x: number) => number,
  a: number,
  b: number,
  n = Math.ceil((b - a) * 128),
): number[] {
  const out: number[] = [];
  const push = (z: number) => {
    if (!out.some((w) => Math.abs(w - z) < 1e-7)) out.push(z);
  };
  let px = a;
  let py = h(a);
  if (Math.abs(py) < 1e-12) push(a);
  for (let i = 1; i <= n; i++) {
    const x = a + ((b - a) * i) / n;
    const y = h(x);
    if (Number.isFinite(y) && Math.abs(y) < 1e-12) push(x);
    else if (Number.isFinite(py) && Number.isFinite(y) && py * y < 0) {
      // bissection
      let lo = px;
      let hi = x;
      let flo = py;
      for (let k = 0; k < 80; k++) {
        const mid = (lo + hi) / 2;
        const fm = h(mid);
        if (!Number.isFinite(fm)) break;
        if (fm === 0) {
          lo = hi = mid;
          break;
        }
        if (flo * fm < 0) hi = mid;
        else {
          lo = mid;
          flo = fm;
        }
      }
      push((lo + hi) / 2);
    }
    px = x;
    py = y;
  }
  // un zéro presque entier vient d'un nœud ou d'une racine exacte : on le recale
  return out
    .map((z) => {
      const r = Math.round(z * 2) / 2;
      return Math.abs(z - r) < 1e-6 && Math.abs(h(r)) < 1e-9 ? r : z;
    })
    .sort((p, q) => p - q);
}

export function solve(s: Scenario, k: number, cmp: Cmp): Solution {
  const g = gOf(s, k);
  const h = (x: number) => s.f.at(x) - g.at(x);
  const [a, b] = searchSpan(s);
  const zeros = zerosOf(h, a, b).filter((z) => inDomain(s, z));
  const holes = s.domain.holes.filter((z) => z > a && z < b);
  // bornes finies du domaine, zéros, valeurs interdites ; les bouts infinis à part
  const finite = [
    ...(s.domain.lo !== null ? [s.domain.lo] : []),
    ...(s.domain.hi !== null ? [s.domain.hi] : []),
    ...zeros,
    ...holes,
  ];
  const cuts = [
    ...(s.domain.lo === null ? [-Infinity] : []),
    ...[...new Set(finite)].sort((p, q) => p - q),
    ...(s.domain.hi === null ? [Infinity] : []),
  ];
  const pieces: Piece[] = [];
  const endBound = (x: number): Bound =>
    Number.isFinite(x)
      ? { v: x, closed: inDomain(s, x) && holds(cmp, h(x)) }
      : { v: x, closed: false };
  /** point test d'un morceau */
  const probe = (lo: number, hi: number) =>
    lo === -Infinity ? (hi === Infinity ? 0 : hi - 1) : hi === Infinity ? lo + 1 : (lo + hi) / 2;

  if (cmp === '=') {
    for (const z of zeros) pieces.push({ lo: { v: z, closed: true }, hi: { v: z, closed: true } });
  } else {
    for (let i = 0; i + 1 < cuts.length; i++) {
      const lo = cuts[i]!;
      const hi = cuts[i + 1]!;
      const hm = h(probe(lo, hi));
      if (!Number.isFinite(hm) || !holds(cmp, hm, 0)) continue;
      const piece = { lo: endBound(lo), hi: endBound(hi) };
      const last = pieces.at(-1);
      // deux morceaux qui se touchent en un point gardé ne font qu'un
      if (last && last.hi.v === piece.lo.v && last.hi.closed) last.hi = piece.hi;
      else pieces.push(piece);
    }
    // points isolés : contact (≤ ou ≥) sans morceau autour, ou borne du domaine seule
    if (cmp === '<=' || cmp === '>=')
      for (const x of cuts) {
        if (!Number.isFinite(x) || !inDomain(s, x) || !holds(cmp, h(x))) continue;
        const covered = pieces.some(
          (p) =>
            (p.lo.v < x && x < p.hi.v) ||
            (p.lo.v === x && p.lo.closed) ||
            (p.hi.v === x && p.hi.closed),
        );
        if (!covered) pieces.push({ lo: { v: x, closed: true }, hi: { v: x, closed: true } });
      }
    pieces.sort((p, q) => p.lo.v - q.lo.v);
  }
  return { pieces, zeros, ...setText(pieces, digitsOf(s)) };
}

/** lecture d'une courbe donnée : au dixième ; d'une formule : au centième */
export const digitsOf = (s: Scenario) => (s.f.nodes ? 1 : 2);

/** « S = [−4 ; −3] ∪ {1} », « S = {−1 ; 2} », « S = ∅ », « S ≈ … » */
export function setText(
  pieces: readonly Piece[],
  approxDigits = 1,
): { text: string; approx: boolean } {
  let approx = false;
  const b = (v: number) => {
    if (v === -Infinity) return '−∞';
    if (v === Infinity) return '+∞';
    const e = exactText(v, approxDigits);
    if (!e.exact) approx = true;
    return e.text;
  };
  if (pieces.length === 0) return { text: `S${NB}=${NB}∅`, approx: false };
  if (pieces.length === 1 && pieces[0]!.lo.v === -Infinity && pieces[0]!.hi.v === Infinity)
    return { text: `S${NB}=${NB}ℝ`, approx: false };
  const parts: string[] = [];
  let points: string[] = [];
  const flush = () => {
    if (points.length) parts.push(`{${[...new Set(points)].join(`${NB};${NB}`)}}`);
    points = [];
  };
  for (const p of pieces) {
    if (p.lo.v === p.hi.v) {
      points.push(b(p.lo.v));
      continue;
    }
    flush();
    parts.push(
      `${p.lo.closed ? '[' : ']'}${b(p.lo.v)}${NB};${NB}${b(p.hi.v)}${p.hi.closed ? ']' : '['}`,
    );
  }
  flush();
  return { text: `S${NB}${approx ? '≈' : '='} ${parts.join(' ∪ ')}`, approx };
}

/** x est-il solution ? (pour le curseur) */
export function isSolution(s: Scenario, k: number, cmp: Cmp, x: number): boolean | null {
  if (!inDomain(s, x)) return null;
  const v = s.f.at(x) - gOf(s, k).at(x);
  if (!Number.isFinite(v)) return null;
  return holds(cmp, v, 1e-9);
}

/* ---------- consignes ---------- */

/** la seconde fonction est-elle une constante (droite y = k, ou 0) ? sa valeur */
export const constantOf = (s: Scenario, k: number): number | null =>
  !s.g ? k : s.g.formula === '0' ? 0 : null;

/** second membre écrit : « 2 », « 0 », « g(x) » */
export const rhsText = (s: Scenario, k: number) => {
  const c = constantOf(s, k);
  return c === null ? 'g(x)' : num(c);
};

/** « f(x) ≥ 2 », « f(x) < g(x) », « f(x) > 0 » */
export function inequationText(s: Scenario, k: number, cmp: Cmp): string {
  return `f(x) ${CMP_TEXT[cmp]} ${rhsText(s, k)}`;
}

/** ce qu'on lit sur la figure */
export function readingText(s: Scenario, k: number, cmp: Cmp): string {
  const other = s.g
    ? s.g.formula === '0'
      ? 'l’axe des abscisses'
      : 'la courbe de g'
    : `la droite d’équation y = ${num(k)}`;
  switch (cmp) {
    case '=':
      return s.g
        ? `On cherche les abscisses des points communs à la courbe de f et à ${other}.`
        : `On cherche les abscisses des points de la courbe de f situés sur ${other} : les antécédents de ${num(k)} par f.`;
    case '<':
      return `On garde les x où la courbe de f est strictement en dessous de ${other}.`;
    case '<=':
      return `On garde les x où la courbe de f est en dessous de ${other} ou la rencontre.`;
    case '>':
      return `On garde les x où la courbe de f est strictement au-dessus de ${other}.`;
    case '>=':
      return `On garde les x où la courbe de f est au-dessus de ${other} ou la rencontre.`;
  }
}

/**
 * En 3e, pas d'intervalles : les solutions d'une équation en liste,
 * « −3, −1 et 3 », « 2 », « aucune ».
 */
export function listText(pieces: readonly Piece[], digits = 1): string {
  const xs = [...new Set(pieces.filter((p) => p.lo.v === p.hi.v).map((p) => p.lo.v))];
  if (!xs.length) return 'Aucune solution';
  const ts = xs.map((v) => {
    const e = exactText(v, digits);
    return e.exact ? e.text : `≈${NB}${e.text}`;
  });
  const joined = ts.length === 1 ? ts[0]! : `${ts.slice(0, -1).join(', ')} et ${ts.at(-1)}`;
  return `${ts.length === 1 ? 'Solution' : 'Solutions'} : ${joined}`;
}

/* ---------- défis : ensembles proposés ---------- */

export type MistakeKind = 'strict' | 'reverse' | 'yread' | 'image' | 'domain' | 'missing' | 'other';

export interface SetOption {
  text: string;
  correct: boolean;
  kind: MistakeKind | null;
}

const flipStrict: Partial<Record<Cmp, Cmp>> = { '<': '<=', '<=': '<', '>': '>=', '>=': '>' };
const reverse: Partial<Record<Cmp, Cmp>> = { '<': '>', '<=': '>=', '>': '<', '>=': '<=' };

export const MISTAKE_TEXT: Record<MistakeKind, string> = {
  strict:
    'Attention aux crochets et aux points isolés : avec ≤ ou ≥, les abscisses des points communs sont solutions (crochet fermé) ; avec < ou >, elles ne le sont pas.',
  reverse: 'C’est l’inverse : cet ensemble garde les x où la courbe de f est de l’autre côté.',
  yread:
    'Les solutions sont des abscisses : on les lit sur l’axe horizontal, pas sur l’axe des ordonnées.',
  image:
    'Image et antécédent sont confondus : f(k) est l’image de k ; on cherche ici les x dont l’image vaut k, en partant de l’axe des ordonnées.',
  domain:
    'Regarde l’ensemble de définition : les solutions n’en sortent pas, et une valeur interdite reste exclue.',
  missing: 'Il manque des solutions : parcours toute la courbe, d’un bout à l’autre.',
  other: 'Ce n’est pas ce que montre la figure : compare les deux courbes abscisse par abscisse.',
};

/**
 * Les réponses proposées : la bonne et jusqu'à trois erreurs classiques
 * (crochets, sens de l'inégalité, ordonnées au lieu des abscisses, domaine
 * oublié, solution oubliée). `list` : écriture de 3e (équations seulement).
 */
export function options(
  s: Scenario,
  k: number,
  cmp: Cmp,
  rnd: () => number,
  style: 'set' | 'list' = 'set',
): SetOption[] {
  const sol = solve(s, k, cmp);
  const digits = digitsOf(s);
  const write = (pieces: readonly Piece[]) =>
    style === 'list' ? listText(pieces, digits) : setText(pieces, digits).text;
  const right = write(sol.pieces);
  const seen = new Set([right.replace(/\s/g, '')]);
  const out: SetOption[] = [{ text: right, correct: true, kind: null }];
  const add = (pieces: Piece[] | null, kind: MistakeKind, raw?: string) => {
    const text = raw ?? (pieces ? write(pieces) : null);
    if (!text) return;
    const key = text.replace(/\s/g, '');
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ text, correct: false, kind });
  };
  const point = (v: number): Piece => ({ lo: { v, closed: true }, hi: { v, closed: true } });
  const f = flipStrict[cmp];
  if (f) add(solve(s, k, f).pieces, 'strict');
  const r = reverse[cmp];
  if (r) add(solve(s, k, r).pieces, 'reverse');
  // ordonnées au lieu des abscisses
  const c = constantOf(s, k);
  if (c !== null) {
    const [a, b] = visibleSpan(s);
    const ys: number[] = [];
    for (let i = 0; i <= 400; i++) {
      const y = s.f.at(a + ((b - a) * i) / 400);
      if (Number.isFinite(y)) ys.push(y);
    }
    const lo = Math.min(...ys);
    const hi = Math.max(...ys);
    const kk = { v: c, closed: cmp !== '<' && cmp !== '>' };
    if (cmp === '=') {
      add([point(c)], 'yread');
      // l'image de k au lieu de ses antécédents
      const fk = inDomain(s, c) ? s.f.at(c) : NaN;
      if (Number.isFinite(fk) && Math.abs(fk * 2 - Math.round(fk * 2)) < 1e-9)
        add([point(fk)], 'image');
    } else if (cmp === '>' || cmp === '>=') {
      if (s.f.nodes ? hi > c : true)
        add([{ lo: kk, hi: { v: s.f.nodes ? hi : Infinity, closed: !!s.f.nodes } }], 'yread');
    } else if (s.f.nodes ? lo < c : true)
      add([{ lo: { v: s.f.nodes ? lo : -Infinity, closed: !!s.f.nodes }, hi: kk }], 'yread');
  } else if (sol.zeros.length) {
    // deux courbes : seules les abscisses des points communs deviennent leurs ordonnées
    const isZero = (v: number) => sol.zeros.includes(v);
    const ok = sol.pieces.every(
      (p) =>
        (!Number.isFinite(p.lo.v) || isZero(p.lo.v)) &&
        (!Number.isFinite(p.hi.v) || isZero(p.hi.v)),
    );
    const g = gOf(s, k);
    const mapV = (v: number) => (Number.isFinite(v) ? g.at(v) : v);
    const pieces = sol.pieces.map((p) => ({
      lo: { ...p.lo, v: mapV(p.lo.v) },
      hi: { ...p.hi, v: mapV(p.hi.v) },
    }));
    if (ok && pieces.every((p) => p.lo.v <= p.hi.v)) add(pieces, 'yread');
  }
  // domaine : bornes finies remplacées par l'infini, ou valeur interdite oubliée
  if (style === 'set' && (s.domain.lo !== null || s.domain.hi !== null)) {
    const pieces = sol.pieces.map((p) => ({
      lo:
        s.domain.lo !== null && p.lo.v === s.domain.lo && p.lo.v !== p.hi.v
          ? { v: -Infinity, closed: false }
          : p.lo,
      hi:
        s.domain.hi !== null && p.hi.v === s.domain.hi && p.lo.v !== p.hi.v
          ? { v: Infinity, closed: false }
          : p.hi,
    }));
    add(pieces, 'domain');
  }
  // valeur interdite prise pour une solution : crochet fermé sur elle
  if (style === 'set' && s.domain.holes.length) {
    const at = (bd: Bound) => (s.domain.holes.includes(bd.v) ? { v: bd.v, closed: true } : bd);
    add(
      sol.pieces.map((p) => ({ lo: at(p.lo), hi: at(p.hi) })),
      'domain',
    );
  }
  // une solution oubliée
  if (sol.pieces.length > 1) add(sol.pieces.slice(0, -1), 'missing');
  if (sol.pieces.length > 1) add(sol.pieces.slice(1), 'missing');
  if (out.length < 4) add([], 'other');
  if (out.length < 4 && style === 'set') add(null, 'other', `S${NB}=${NB}${s.domain.text}`);
  // la bonne réponse et trois erreurs, dans le désordre
  const chosen = [out[0]!, ...shuffle(out.slice(1), rnd).slice(0, 3)];
  return shuffle(chosen, rnd);
}

function shuffle<T>(list: readonly T[], rnd: () => number): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export interface Question {
  scenario: Scenario;
  k: number;
  cmp: Cmp;
}

/** solutions lisibles au demi-carreau, à au moins un demi de chaque bord du cadre */
export function readable(s: Scenario, k: number): boolean {
  const z = solve(s, k, '=').zeros;
  return (
    z.length > 0 &&
    z.every(
      (x) =>
        Math.abs(x * 2 - Math.round(x * 2)) < 1e-9 &&
        x >= s.window.xMin + 0.5 &&
        x <= s.window.xMax - 0.5,
    )
  );
}

/** les signes du niveau : en 3e, les équations seulement (antécédents) */
export const cmpsFor = (rank: number): Cmp[] => (rank <= 4 ? ['='] : ['=', '<', '<=', '>', '>=']);

/**
 * Une question de défi : un scénario du niveau, un signe du niveau, un k
 * dont les solutions se lisent au demi-carreau, et quatre réponses
 * différentes. Les questions qui ne s'y prêtent pas sont écartées.
 */
const CANDIDATES = new Map<number, Question[]>();

/** toutes les questions possibles d'un niveau (calculées une fois) */
export function questionsFor(rank: number): Question[] {
  const memo = CANDIDATES.get(rank);
  if (memo) return memo;
  const list = SCENARIOS.filter((s) => s.minRank <= rank && rank <= s.maxRank);
  const cmps = cmpsFor(rank);
  const style = rank <= 4 ? 'list' : 'set';
  const out: Question[] = [];
  for (const scenario of list) {
    const ks: number[] = [];
    if (scenario.k) for (let v = scenario.k.min; v <= scenario.k.max + 1e-9; v += 0.5) ks.push(v);
    else ks.push(0);
    for (const k of ks) {
      if (!readable(scenario, k)) continue;
      for (const cmp of cmps)
        if (options(scenario, k, cmp, () => 0, style).length === 4) out.push({ scenario, k, cmp });
    }
  }
  CANDIDATES.set(rank, out);
  return out;
}

export function makeQuestion(rank: number, rnd: () => number): Question {
  const candidates = questionsFor(rank);
  // un scénario au hasard, puis un signe et un k parmi ceux qui conviennent
  const ids = [...new Set(candidates.map((q) => q.scenario.id))];
  const id = ids[Math.floor(rnd() * ids.length)]!;
  const mine = candidates.filter((q) => q.scenario.id === id);
  return mine[Math.floor(rnd() * mine.length)]!;
}
